import { Gauge, Clock, Copy, Check, TrendingDown, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import PriceChart from './PriceChart';
import CarThumbnail from './CarThumbnail';
import SubscribeButton from './SubscribeButton';
import MaxPriceTooltip from './MaxPriceTooltip';
import { useAuth } from '../contexts/AuthContext';
import { useDolarBlue } from '../hooks/useDolarBlue';
import { calcNormalizedChange } from '../utils/priceUtils';

const CarCard = ({ car, onView, onEdit, onDelete, onProspect, readOnly = false }) => {
  const [copied, setCopied] = useState(false);
  const { userProfile } = useAuth();
  const { convertArsToUsd, cotizacion } = useDolarBlue();

  const copyToClipboard = async (text, e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatPrice = (price) => new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0 }).format(price);

  const formatKms = (kms) => new Intl.NumberFormat('es-AR').format(kms) + ' km';

  const formatDaysOld = (days) => {
    if (!days || days === 0) return 'Nuevo';
    if (days < 30) return `${days}d`;
    if (days < 365) return `${Math.floor(days / 30)}m`;
    return `${Math.floor(days / 365)}a`;
  };

  const pct = calcNormalizedChange(car, cotizacion);
  const hasChange = pct != null && pct !== 0;

  return (
    <div
      className="group bg-[#1c1c1a] rounded-lg overflow-hidden h-full cursor-pointer border border-[#2a2926] hover:border-[#3a3835] transition-all duration-200 hover:-translate-y-px"
      onClick={() => onView(car)}
    >
      <div className="overflow-hidden">
        <div className="transition-[filter] duration-300 group-hover:brightness-105">
          <CarThumbnail imageUrl={car.pictures} title={car.title} className="rounded-t-lg" />
        </div>
      </div>

      <div className="p-3 lg:p-5 flex flex-col gap-2 lg:gap-3">
        <div>
          <h3 className="text-xs lg:text-base font-semibold text-[#e8e4de] line-clamp-2 leading-snug">
            {car.title}
          </h3>
          <p className="text-[11px] lg:text-sm text-[#8a8577] mt-0.5">{car.city}</p>
        </div>

        <div className="flex items-baseline gap-2">
          <span className="text-base lg:text-2xl font-bold font-mono text-[#e8e4de] tracking-tight">
            <span className="text-[#8a8577] font-normal text-sm lg:text-lg">
              {car.currency_id === 'ARS' ? '$' : 'US$'}
            </span>
            {' '}{formatPrice(car.price)}
          </span>
          {hasChange && (
            <span className={`flex items-center gap-0.5 text-[11px] font-mono ${pct < 0 ? 'text-[#48a877]' : 'text-[#c75d5d]'}`}>
              {pct < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
              {Math.abs(pct).toFixed(1)}%
            </span>
          )}
        </div>

        {car.currency_id === 'ARS' && convertArsToUsd(car.price) && (
          <p className="text-[11px] text-[#8a8577] -mt-1">
            ≈ US$ {new Intl.NumberFormat('es-AR').format(convertArsToUsd(car.price))}
            <span className="ml-1 opacity-60">Blue</span>
          </p>
        )}

        <MaxPriceTooltip car={car} />

        {car.price_history && car.price_history.length > 1 && 
         car.price_history.some(p => p.price !== car.price_history[0].price) && (
          <div className="py-1">
            <PriceChart priceHistory={car.price_history} currentPrice={car.price} currency={car.currency_id} />
          </div>
        )}

        <div className="flex items-center gap-3 text-[11px] lg:text-sm text-[#8a8577]">
          <span className="flex items-center gap-1">
            <Gauge className="w-3.5 h-3.5" />
            <span className="font-mono">{formatKms(car.kilometers)}</span>
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span className="font-mono">{formatDaysOld(car.days_old)}</span>
          </span>
          {car.vehicle_year && (
            <span className="ml-auto font-mono">{car.vehicle_year}</span>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2 mt-auto">
          {userProfile && (
            <SubscribeButton vehicleId={car.id} vehicleType="car" />
          )}
          <button
            onClick={(e) => copyToClipboard(car.id, e)}
            className="ml-auto text-[#8a8577] hover:text-[#e8e4de] transition-colors"
            title={copied ? "¡Copiado!" : "Copiar ID"}
          >
            {copied ? <Check className="w-3.5 h-3.5 text-[#48a877]" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarCard;
